import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogIn,
  LogOut,
  Loader2,
  Heart,
  ShoppingBag,
  Mail,
  ShieldCheck,
  Calendar,
} from 'lucide-react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useUser from '@/hooks/useUser';
import useWishlist from '@/hooks/useWishlist';
import {
  processSessionId,
  startCustomerGoogleSignIn,
} from '@/lib/bothyAuth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/*
 * /account — customer-facing account page.
 *
 *  • If the URL fragment carries a session_id, exchange it via the
 *    shared /api/auth/session endpoint, then refresh() the context and
 *    strip the fragment from the URL.
 *  • If the user is signed in, show their profile card and stubbed
 *    order-history / wishlist sections.
 *  • If they're not signed in, show the marketing sign-in prompt.
 *
 *  REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 *  URLS, THIS BREAKS THE AUTH.
 */

export default function AccountPage() {
  const navigate = useNavigate();
  const { user, loading, refresh, signOut } = useUser();
  const hasProcessed = useRef(false);
  const [exchanging, setExchanging] = useState(
    typeof window !== 'undefined'
      ? window.location.hash?.includes('session_id=')
      : false,
  );
  const [exchangeError, setExchangeError] = useState(null);

  // OAuth return handler — runs once when we arrive back from Emergent
  useEffect(() => {
    if (hasProcessed.current) return;
    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match?.[1];
    if (!sessionId) return;
    hasProcessed.current = true;

    (async () => {
      try {
        await processSessionId(sessionId);
        window.history.replaceState(null, '', '/account');
        await refresh();
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Sign-in failed';
        setExchangeError(detail);
      } finally {
        setExchanging(false);
      }
    })();
  }, [refresh]);

  // ─────────── Loading spinner while we sort out session state ──────────
  if (loading || exchanging) {
    return (
      <>
        <Navigation />
        <main
          data-testid="account-loading"
          className="min-h-[70vh] grid place-items-center bg-[var(--brand-parchment)]"
        >
          <div className="flex items-center gap-3 text-[var(--brand-olive)]">
            <Loader2 className="animate-spin" size={22} />
            <span className="text-sm">Just a moment&hellip;</span>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ─────────── Anonymous — sign-in prompt ──────────
  if (!user) {
    return (
      <>
        <Navigation />
        <main
          data-testid="account-signin"
          className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pb-24"
        >
          <div className="max-w-lg mx-auto px-6 lg:px-10 pt-48 pb-24 text-center">
            <span className="font-eyebrow !text-[var(--brand-olive)]">
              Your account
            </span>
            <h1 className="mt-3 font-display text-4xl sm:text-5xl text-[var(--brand-forest-deep)] leading-tight">
              Sign in to save your kit.
            </h1>
            <p className="mt-5 text-[var(--brand-ink)] leading-relaxed">
              Signing in with Google keeps your wishlist across devices, tucks
              your orders together in one place, and lets us pick up where we
              left off next time you enquire. It&rsquo;s the same account you
              already use with Google &mdash; no new password to remember.
            </p>

            {exchangeError && (
              <div
                data-testid="account-signin-error"
                className="mt-6 bg-[var(--brand-oak)]/10 border border-[var(--brand-oak)]/30 px-4 py-3 text-sm text-left text-[var(--brand-oak)]"
              >
                {exchangeError}
              </div>
            )}

            <button
              data-testid="account-google-signin"
              onClick={startCustomerGoogleSignIn}
              className="mt-10 btn-gold inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold rounded-none"
            >
              <LogIn size={16} /> Sign in with Google
            </button>

            <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[var(--brand-muted)]">
              We only see your name, email &amp; profile photo.
              <br />
              Nothing you don&rsquo;t already share with any shop online.
            </p>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  // ─────────── Signed in — profile + stubbed panels ──────────
  return (
    <>
      <Navigation />
      <main
        data-testid="account-page"
        className="bg-[var(--brand-parchment)] text-[var(--brand-forest)] pb-24 pt-32"
      >
        <section className="max-w-4xl mx-auto px-6 lg:px-10">
          {/* Profile card */}
          <div
            data-testid="account-profile"
            className="bg-[var(--brand-surface)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <div className="shrink-0">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-20 h-20 object-cover border-2 border-[var(--brand-gold)]"
                />
              ) : (
                <div className="w-20 h-20 grid place-items-center bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] font-display text-3xl border-2 border-[var(--brand-gold)]">
                  {user.name?.[0]?.toUpperCase() || '·'}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-eyebrow !text-[var(--brand-olive)]">
                Signed in
              </span>
              <h1 className="mt-1 font-display text-3xl sm:text-4xl text-[var(--brand-forest-deep)] leading-tight">
                {user.name}
              </h1>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[var(--brand-muted)]">
                <span className="inline-flex items-center gap-1.5">
                  <Mail size={13} /> {user.email}
                </span>
                {user.is_admin && (
                  <Link
                    to="/the-bothy/dashboard"
                    data-testid="account-bothy-link"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold bg-[var(--brand-forest-deep)] text-[var(--brand-gold)] px-2.5 py-1 border-2 border-[var(--brand-gold)]"
                  >
                    <ShieldCheck size={12} /> The Bothy
                  </Link>
                )}
              </div>
            </div>
            <button
              data-testid="account-signout"
              onClick={async () => {
                await signOut();
                navigate('/');
              }}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-forest-deep)] hover:text-[var(--brand-oak)]"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>

          {/* Live account panels — orders + wishlist */}
          <div className="mt-10 grid md:grid-cols-2 gap-6">
            <OrdersPanel />
            <SavedKitPanel />
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

// ─────────── Panels ───────────

function OrdersPanel() {
  const [state, setState] = useState({ loading: true, items: [], error: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/account/orders`, {
          withCredentials: true,
        });
        if (!cancelled) setState({ loading: false, items: res.data?.items || [], error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, items: [], error: e?.response?.data?.detail || 'Could not load orders.' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div
      data-testid="account-orders"
      className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/20 p-6"
    >
      <div className="flex items-center gap-2 text-[var(--brand-forest-deep)]">
        <ShoppingBag size={18} strokeWidth={2.25} />
        <h2 className="font-display text-2xl">Your orders</h2>
      </div>
      {state.loading ? (
        <p className="mt-4 text-sm text-[var(--brand-muted)]">Loading&hellip;</p>
      ) : state.error ? (
        <p className="mt-4 text-sm text-[var(--brand-oak)]">{state.error}</p>
      ) : state.items.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--brand-muted)] leading-relaxed">
          Nothing here yet. When you order, it&rsquo;ll appear here with dispatch
          notes and the same email confirmation we send.
        </p>
      ) : (
        <ul className="mt-4 space-y-3" data-testid="account-orders-list">
          {state.items.map((o) => (
            <li
              key={o.session_id || o.id}
              data-testid={`account-order-${o.slug}`}
              className="bg-[var(--brand-surface)] border border-[var(--brand-olive)]/20 border-l-4 border-l-[var(--brand-gold)] px-4 py-3"
            >
              <div className="flex items-baseline justify-between gap-3">
                <div className="font-display text-lg text-[var(--brand-forest-deep)] leading-tight">
                  {o.product_name}
                </div>
                <div className="font-mono text-sm font-semibold text-[var(--brand-forest-deep)] whitespace-nowrap">
                  £{Number(o.amount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.16em] text-[var(--brand-muted)]">
                <span className="inline-flex items-center gap-1"><Calendar size={11} /> {new Date(o.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                <span className="opacity-40">·</span>
                <span className="text-[var(--brand-olive)] font-semibold">
                  {(o.fulfilment_status || 'paid').replace(/_/g, ' ')}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SavedKitPanel() {
  const { items, remove } = useWishlist();
  return (
    <div
      data-testid="account-wishlist"
      className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/20 p-6"
    >
      <div className="flex items-center gap-2 text-[var(--brand-forest-deep)]">
        <Heart size={18} strokeWidth={2.25} />
        <h2 className="font-display text-2xl">Saved kit</h2>
      </div>
      {items.length === 0 ? (
        <p className="mt-3 text-sm text-[var(--brand-muted)] leading-relaxed">
          Nothing saved yet. Tap the heart on any product to keep it here &mdash;
          it&rsquo;ll follow you to whichever device you sign in on next.
        </p>
      ) : (
        <ul className="mt-4 space-y-2" data-testid="account-wishlist-list">
          {items.map((it) => (
            <li
              key={it.slug}
              data-testid={`account-wishlist-${it.slug}`}
              className="flex items-center gap-3 bg-[var(--brand-surface)] border border-[var(--brand-olive)]/20 px-3 py-2"
            >
              {it.image && (
                <img src={it.image} alt="" className="w-12 h-12 object-cover shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="font-display text-base text-[var(--brand-forest-deep)] truncate">{it.name || it.slug}</div>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--brand-olive)]">{it.price || '—'}</div>
              </div>
              <button
                type="button"
                aria-label={`Remove ${it.name || it.slug}`}
                onClick={() => remove(it.slug)}
                className="text-xs text-[var(--brand-muted)] hover:text-[var(--brand-oak)] font-semibold uppercase tracking-[0.14em]"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StubPanel({ icon: Icon, title, body, testId }) {
  return (
    <div
      data-testid={testId}
      className="bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/20 p-6"
    >
      <div className="flex items-center gap-2 text-[var(--brand-forest-deep)]">
        <Icon size={18} strokeWidth={2.25} />
        <h2 className="font-display text-2xl">{title}</h2>
      </div>
      <p className="mt-3 text-sm text-[var(--brand-muted)] leading-relaxed">
        {body}
      </p>
      <div className="mt-4 inline-flex items-center text-[10px] uppercase tracking-[0.22em] font-bold bg-[var(--brand-gold)]/20 text-[var(--brand-forest-deep)] px-2.5 py-1">
        Coming soon
      </div>
    </div>
  );
}
